import { Typography } from '@mui/material';

import {
  AutoSaveList,
  EMPTY_THERAPY_VALUES,
  NoTherapyCheckbox,
  NoTherapyConfirmDialog,
  TherapyFields,
  getTherapyDeleteMessage,
  getTherapyRowLabel,
  mapTherapyRowToValues,
  useFormReadOnly,
  useNoTherapyConfirm,
} from '../../../../common';
import { THERAPY_FORM_SCHEMA } from './validationSchema';
import { toTreatmentPlanV3TherapyFieldPayload } from './utils';
import { useFetchTreatmentPlanV3TherapyPage, useTreatmentPlanV3Therapies } from './hooks';
import { TreatmentPlanV3PageContent, TreatmentPlanV3PageLoadState } from '../components';
import { TREATMENT_PLAN_V3_SECTION_HEADING_SX } from '../../../styles';
import { MONTH_YEAR_DISPLAY_FORMAT } from '../../../../../constants';

import type { ITherapy, TherapyFormValues, TherapyPayload } from '../../../../common';

export default function TherapyForm() {
  const { retry } = useFetchTreatmentPlanV3TherapyPage();
  const {
    therapies,
    noTherapy,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    setNoTherapies,
  } = useTreatmentPlanV3Therapies();
  const readOnly = useFormReadOnly();
  const hasSavedTherapies = therapies.length > 0;
  const { isConfirmOpen, handleToggle, handleConfirm, closeConfirm } = useNoTherapyConfirm({
    hasSavedTherapies,
    setNoTherapies,
  });

  return (
    <TreatmentPlanV3PageContent title="Historical &amp; present therapies">
      <Typography variant="h6" sx={TREATMENT_PLAN_V3_SECTION_HEADING_SX}>
        Child historical and present ABA and other therapies
      </Typography>
      <TreatmentPlanV3PageLoadState
        errorMessage="Unable to load therapies. Please try again."
        hasLoaded={hasLoaded}
        hasLoadError={hasLoadError}
        onRetry={retry}
      >
        <NoTherapyCheckbox
          checked={noTherapy}
          disabled={isLoading || readOnly || isSaving}
          onChange={handleToggle}
        />

        {noTherapy ? null : (
          <AutoSaveList<TherapyFormValues, ITherapy, TherapyPayload>
            addButtonLabel="Add therapy"
            disabled={isLoading || isBulkSaving}
            emptyValues={EMPTY_THERAPY_VALUES}
            entries={therapies}
            getDeleteConfirm={(row) => ({
              title: 'Confirm therapy deletion?',
              message: getTherapyDeleteMessage(row),
            })}
            getRowKey={(row) => row.id}
            getRowLabel={getTherapyRowLabel}
            isSaving={isSaving}
            mapRowToValues={mapTherapyRowToValues}
            newEntryLabel="New therapy"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            plainDraft
            renderFields={({
              formik,
              disabled,
              readOnly: fieldsReadOnly,
              saveField,
              backgroundColor,
            }) => (
              <TherapyFields
                backgroundColor={backgroundColor}
                disabled={disabled}
                formik={formik}
                includeRecordShared
                monthFormat={MONTH_YEAR_DISPLAY_FORMAT}
                readOnly={fieldsReadOnly}
                saveField={saveField}
              />
            )}
            toFieldPayload={toTreatmentPlanV3TherapyFieldPayload}
            validationSchema={THERAPY_FORM_SCHEMA}
          />
        )}

        <NoTherapyConfirmDialog
          open={isConfirmOpen}
          hasSavedTherapies={hasSavedTherapies}
          confirmLoading={isSaving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      </TreatmentPlanV3PageLoadState>
    </TreatmentPlanV3PageContent>
  );
}
